
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { optimizeBiographyForDeepIntegration, validateBiographyQuality } from '@/utils/smartBiographyOptimization';
import { buildSeedAnchors } from '@/utils/anchorQA';
import { parseBiographyVariables } from '@/utils/biographyParser';
import { useOptimizedUserPreferences } from '@/hooks/useOptimizedUserPreferences';

interface AIQualityResult {
  quality_score: number;
  has_relationships: boolean;
  has_personal_spaces: boolean;
  has_emotional_content: boolean;
  has_work_context: boolean;
  has_goals: boolean;
  recommendations: string[];
  processing_time_ms: number;
  cache_hit: boolean;
}

export const useBiographyQuality = (biography: string, categoryId: string = 'biography', sessionType: string = 'personalized') => {
  const { toast } = useToast();
  const { updatePreference } = useOptimizedUserPreferences();
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedBiography, setOptimizedBiography] = useState<string>('');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // AI quality state
  const [aiQuality, setAiQuality] = useState<AIQualityResult | null>(null);
  const [loadingQuality, setLoadingQuality] = useState(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const lastBiographyRef = useRef<string>('');

  // Local quality for immediate feedback (fallback while AI loads)
  const localQuality = useMemo(() => {
    return validateBiographyQuality(biography || '', categoryId);
  }, [biography, categoryId]);

  // Fetch AI quality with debouncing
  const fetchAIQuality = useCallback(async (bio: string) => {
    if (!bio || bio.trim().length < 10) {
      setAiQuality(null);
      return;
    }

    setLoadingQuality(true);
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${backendUrl}/biographies/quality`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ biography: bio }),
      });

      if (!response.ok) {
        console.warn('AI quality assessment failed, using local fallback');
        return;
      }

      const data: AIQualityResult = await response.json();
      setAiQuality(data);
    } catch (error) {
      console.warn('AI quality assessment error, using local fallback:', error);
    } finally {
      setLoadingQuality(false);
    }
  }, []);

  // Debounced effect to fetch AI quality when biography changes
  useEffect(() => {
    // Skip if biography hasn't actually changed
    if (biography === lastBiographyRef.current) {
      return;
    }
    lastBiographyRef.current = biography;

    // Clear any pending debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Skip AI call for very short biographies
    if (!biography || biography.trim().length < 10) {
      setAiQuality(null);
      return;
    }

    // Debounce AI quality fetch (1.5 seconds after user stops typing)
    debounceTimer.current = setTimeout(() => {
      fetchAIQuality(biography);
    }, 1500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [biography, fetchAIQuality]);

  const seedAnchors = useMemo(() => {
    const vars = parseBiographyVariables(biography || '');
    return buildSeedAnchors(vars);
  }, [biography]);

  const optimizeAndSave = async () => {
    if (!biography || biography.trim().length === 0) {
      toast({ title: 'Biography empty', description: 'Add some details first to optimize.' });
      return;
    }

    if (biography.trim().length < 50) {
      toast({ title: 'Biography too short', description: 'Please add more details (at least 50 characters) to optimize.' });
      return;
    }

    try {
      setOptimizing(true);
      const optimized = await optimizeBiographyForDeepIntegration(biography, categoryId, sessionType);
      setOptimizedBiography(optimized);

      // Save to user_preferences as an optimized biography artifact
      updatePreference({
        category: 'biography',
        preferenceType: 'optimized_biography',
        value: optimized,
        confidenceBoost: 0.2,
      });
      const savedAt = new Date().toISOString();
      setLastSavedAt(savedAt);
      toast({ title: 'Biography optimized', description: 'Your biography has been restructured for better personalization.' });

      // Refresh AI quality for the optimized biography
      fetchAIQuality(optimized);
    } catch (e: any) {
      // AI-specific error handling based on HTTP status codes
      const status = e.status || e.response?.status;

      if (status === 429) {
        toast({
          title: 'Please wait',
          description: 'You can optimize again in a moment.',
          variant: 'destructive'
        });
      } else if (status === 503) {
        toast({
          title: 'Service unavailable',
          description: 'AI optimization is temporarily unavailable. Please try again later.',
          variant: 'destructive'
        });
      } else if (status === 400) {
        toast({
          title: 'Invalid biography',
          description: e.message || 'Please check your biography and try again.',
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Optimization failed',
          description: e.message || 'Please try again later.',
          variant: 'destructive'
        });
      }
    } finally {
      setOptimizing(false);
    }
  };

  // Use AI quality if available, otherwise fall back to local
  const qualityScore = aiQuality?.quality_score ?? localQuality.qualityScore;
  const recommendations = aiQuality?.recommendations ?? localQuality.recommendations;

  return {
    qualityScore,
    recommendations,
    seedAnchors,
    optimizedBiography,
    optimizing,
    lastSavedAt,
    optimizeAndSave,
    loadingQuality,
    // Expose AI category flags for potential UI use
    hasRelationships: aiQuality?.has_relationships,
    hasPersonalSpaces: aiQuality?.has_personal_spaces,
    hasEmotionalContent: aiQuality?.has_emotional_content,
    hasWorkContext: aiQuality?.has_work_context,
    hasGoals: aiQuality?.has_goals,
  };
};
